// @ts-expect-error No types available for mixpanel-browser
import mixpanel from 'mixpanel-browser';

export type AnalyticsContext = {
  actorId?: string;
  accountId?: string;
  projectId?: string;
};

/**
 * Restated as literals here — rather than imported from
 * `extractedResourceReview` — because the analytics privacy invariant statically
 * proves every payload property is a bounded union, and it can only resolve
 * aliases declared in this file. Review surfaces pass values typed by the
 * canonical `ReviewableResourceType`, so a type added to that list fails to
 * compile at the `track` call until it is added here too.
 */
export type ReviewableResourceType =
  | 'Condition'
  | 'Observation'
  | 'MedicationStatement'
  | 'MedicationRequest'
  | 'AllergyIntolerance'
  | 'Immunization'
  | 'Procedure';

/**
 * Approved stable route titles supplied to authenticated `PrivateRoute`
 * routes. Sourced from docs/product-analytics/clinician-adoption.md; keep in
 * sync with that contract.
 */
export type ScreenName =
  | 'API Keys'
  | 'Access Control'
  | 'Account Auditing'
  | 'Add Data'
  | 'Add Genomic Action'
  | 'Authentication Client'
  | 'Billing'
  | 'Clinical Trials'
  | 'Cohorts'
  | 'Consent'
  | 'Content Management'
  | 'Create Account'
  | 'Create Ecological Momentary Assessments'
  | 'Create Test Account'
  | 'Custom Apps'
  | 'Daily Priorities'
  | 'EHR'
  | 'EHRs'
  | 'Ecological Momentary Assessments'
  | 'Education Content'
  | 'Employees'
  | 'Features'
  | 'File Transfer'
  | 'File Validation'
  | 'Files'
  | 'Gene Sets'
  | 'Groups'
  | 'Home'
  | 'Insights'
  | 'Invitations'
  | 'Machine Learning'
  | 'Machine Learning Dataset Detail'
  | 'Machine Learning Example'
  | 'Machine Learning Labeling'
  | 'Machine Learning Model Detail'
  | 'Machine Learning Model Run Detail'
  | 'Manage Account'
  | 'Manage Circles'
  | 'Manage Ecological Momentary Assessments'
  | 'Manage Projects'
  | 'Manage Survey'
  | 'Marketplace'
  | 'Member AI Tools'
  | 'Member Action Plan'
  | 'Member Annual Report'
  | 'Member App'
  | 'Member Composite Report'
  | 'Member Details'
  | 'Member Documents'
  | 'Member Labs'
  | 'Member Omics Explorer'
  | 'Member Onboarding Snapshot'
  | 'Member Regenerative Medicine Ops'
  | 'Member Rule Actions Audit'
  | 'Member Surveys'
  | 'Member Zori Pulse'
  | 'Members'
  | 'My Consent'
  | 'Omics'
  | 'Omics Actions'
  | 'Omics Explorer'
  | 'Omics Ingestions'
  | 'Omics Test'
  | 'Omics Tests'
  | 'Onboarding Snapshot'
  | 'Ontologies Local'
  | 'Ontologies Local Code System'
  | 'Ontologies Local Code Systems'
  | 'Ontologies Local Value Set'
  | 'Ontologies Local Value Sets'
  | 'Ontologies Marketplace'
  | 'Ontologies Marketplace Code System'
  | 'Ontologies Marketplace Code Systems'
  | 'Ontologies Marketplace Value Set'
  | 'Ontologies Marketplace Value Sets'
  | 'Patient Details'
  | 'Profile'
  | 'Project'
  | 'Project Knowledge'
  | 'Project Settings'
  | 'Projects'
  | 'Regenerative Medicine Operations'
  | 'Report Template'
  | 'Report Templates'
  | 'Reseller'
  | 'SFTP'
  | 'SFTP User'
  | 'Subjects'
  | 'Survey Response'
  | 'Surveys'
  | 'Tags'
  | 'Task'
  | 'Tasks'
  | 'Usage'
  | 'Users'
  | 'Zoom Appointments'
  | 'Zori AI'
  | 'Zori Chat'
  | 'Zori Report Print View';

export type AnalyticsEvents = {
  screen_view: { screenName: ScreenName };
  zori_pulse_priorities_viewed: {
    view: 'doctor' | 'care-team';
    resultCountBucket: '0' | '1-5' | '6-20' | '21+';
    loadState: 'success' | 'empty' | 'error';
  };
  zori_pulse_member_opened: {
    view: 'doctor' | 'care-team';
    rankBucket: '1-5' | '6+';
    reasonTypes: Array<
      | 'appointment-today'
      | 'appointment-upcoming'
      | 'critical-issue'
      | 'new-data'
    >;
  };
  pulse_scan_requested: {
    entryPoint: 'practitioner_queue' | 'member_pulse' | 'action_plan';
    outcome: 'accepted' | 'error';
  };
  pulse_scan_completed: {
    entryPoint: 'practitioner_queue' | 'member_pulse' | 'action_plan';
    outcome: 'completed' | 'failed' | 'expired';
    durationBucket: '<30s' | '30-120s' | '>120s' | 'unknown';
  };
  pulse_issue_dispositioned: {
    disposition: 'handled' | 'not-useful';
    hasFeedback: boolean;
    outcome: 'success' | 'error';
  };
  pulse_action_completed: {
    actionType: 'send_member_notification' | 'message_care_team';
    outcome: 'delivered' | 'not_delivered' | 'accepted' | 'error';
  };
  pulse_resource_reviewed: {
    resourceType: ReviewableResourceType;
    resolution: 'approved' | 'rejected';
  };
  document_extraction_requested: {
    sourceType: 'document' | 'note' | 'survey';
    mode: 'single' | 'bulk';
    outcome: 'accepted' | 'partial' | 'duplicate' | 'error';
    selectionCountBucket: '1' | '2-5' | '6+';
  };
  document_extraction_completed: {
    sourceType: 'document' | 'note' | 'survey';
    outcome: 'completed' | 'failed' | 'expired';
    durationBucket: '<30s' | '30-120s' | '>120s' | 'unknown';
  };
  document_extraction_resource_review_completed: {
    surface:
      | 'member_clinical_tab'
      | 'extraction_modal'
      | 'extraction_tasks_audit'
      | 'labs_range_view'
      | 'ai_tools';
    resourceType: ReviewableResourceType;
    resolution: 'approved' | 'rejected';
    outcome: 'success' | 'error';
  };
  annual_report_generation_requested: {
    outcome: 'accepted' | 'error';
    isRegenerate: boolean;
    surface: 'annual_report' | 'action_plan';
    hasImpressions: boolean;
  };
  annual_report_generation_completed: {
    outcome: 'completed' | 'failed' | 'expired';
    durationBucket: '<30s' | '30-120s' | '>120s' | 'unknown';
    isRegenerate: boolean;
    surface: 'annual_report' | 'action_plan';
    hasImpressions: boolean;
  };
  zori_report_generation_requested: {
    outcome: 'accepted' | 'error';
    isRegenerate: boolean;
  };
  zori_report_generation_completed: {
    outcome: 'completed' | 'failed' | 'expired';
    durationBucket: '<30s' | '30-120s' | '>120s' | 'unknown';
    isRegenerate: boolean;
  };
  composite_report_generation_requested: {
    outcome: 'accepted' | 'error';
    scope: 'full' | 'section_subset';
  };
  composite_report_generation_completed: {
    outcome: 'completed' | 'failed' | 'expired';
    durationBucket: '<30s' | '30-120s' | '>120s' | 'unknown';
    scope: 'full' | 'section_subset';
  };
  clinical_impression_saved: {
    surface: 'annual_report' | 'zori_template' | 'composite_section';
    outcome: 'success' | 'error';
  };
  action_plan_saved: { outcome: 'success' | 'error' };
  action_plan_created: { outcome: 'success' | 'error' };
  member_app_onboarding_completed: { outcome: 'success' | 'error' };
  member_contact_sync_completed: {
    field: 'email' | 'phone';
    outcome: 'success' | 'error';
  };
  member_survey_assigned: { outcome: 'success' | 'error' };
  mtb_note_persisted: {
    action: 'save_draft' | 'publish' | 'republish';
    outcome: 'success' | 'error';
  };
  member_chart_note_saved: {
    action: 'create' | 'update';
    outcome: 'success' | 'error';
  };
  member_data_ingest_completed: {
    ingestType: 'file' | 'workbook';
    outcome: 'success' | 'error';
  };
  cgm_change_persisted: {
    resourceKind: 'goal' | 'annotation';
    action: 'create' | 'update' | 'delete';
    outcome: 'success' | 'error';
  };
  zori_chat_opened: {
    entryPoint:
      | 'member_header'
      | 'member_landing'
      | 'member_pulse_quick_link'
      | 'global';
  };
  zori_chat_message_sent: { surface: 'clinician' | 'population' };
  zori_chat_response_received: {
    surface: 'clinician' | 'population';
    outcome: 'completed' | 'error';
  };
  zori_chat_feedback_submitted: {
    sentiment: 'positive' | 'negative';
    fixRequested: boolean;
  };
  zori_chat_report_export_completed: { outcome: 'success' | 'error' };
  zoom_transcript_opened: { status: 'MATCHED' | 'UNMATCHED' };
  zoom_transcript_resolution_completed: {
    action: 'associate' | 'disassociate' | 'not_relevant';
    outcome: 'success' | 'error';
  };
  file_validation_resolution_completed: {
    action: 'delete' | 'mark_valid';
    outcome:
      | 'confirmed'
      | 'timeout'
      | 'submission_error'
      | 'confirmation_error';
  };
  annual_report_sign_off_completed: { outcome: 'success' | 'error' };
  zori_report_sign_off_completed: { outcome: 'success' | 'error' };
};

let initializedToken: string | undefined;

const init = (token?: string): void => {
  if (!token) {
    // Unsetting the token is the kill switch; clear delivery even after a
    // prior successful init (App re-runs init when config.mixpanelToken changes).
    initializedToken = undefined;
    return;
  }
  if (token === initializedToken) {
    return;
  }
  mixpanel.init(token, { autotrack: false });
  initializedToken = token;
};

const canDeliver = (actorId?: string): boolean =>
  !!initializedToken && !!actorId;

const setUser = (actorId?: string): void => {
  if (!canDeliver(actorId)) {
    return;
  }
  mixpanel.identify(actorId);
};

const resetUser = (): void => {
  // An uninitialized SDK throws on reset, which would abort logout before
  // auth storage is cleared, so the unset-token kill switch must no-op here
  // exactly as it does for identify and track.
  if (!initializedToken) {
    return;
  }
  mixpanel.reset();
};

const track = <Key extends keyof AnalyticsEvents>(
  name: Key,
  payload: AnalyticsEvents[Key],
  context: AnalyticsContext,
): void => {
  if (!canDeliver(context.actorId)) {
    return;
  }
  // SDK delivery can throw; callers put track inside product try/catch, so
  // an escaping exception would flip success into error UI and false analytics.
  try {
    mixpanel.track(name, {
      accountId: context.accountId,
      projectId: context.projectId,
      ...payload,
    });
  } catch {
    // no-op
  }
};

export const analytics = {
  init,
  setUser,
  resetUser,
  track,
};
